import React, { useState } from "react";

import { Button } from "../common/design-system/Button";
import { Input } from "../common/design-system/Input";
import { images } from "../../assets";
import DownChevIcon from "../../assets/svgIcons/down-chev-icon.svg";
import { cn } from "../common/design-system/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../common/design-system/Popover";
import WebScraperTabs from "./WebScraperTabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../common/design-system/Dropdown";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "../common/design-system/Dialog";
import SuccessState from "../common/SuccessState";
import {
  DEFAULT_CHUNK_SIZE,
  DEFAULT_OVERLAP_SIZE,
  DEFAULT_RECURSION_DEPTH,
  ENV,
  MAX_PAGES_TO_SCRAPE,
  MAX_RECURSION_DEPTH,
} from "../../constants/shared";
import { useCarbon } from "../../context/CarbonContext";
import type { BannerState } from "../common/Banner";
import {
  getBaseURL,
  isValidHttpUrl,
  removeHttp,
} from "../../utils/helper-functions";
import type { WebScraperIntegration } from "../../typing/shared";
import { IntegrationName, ActionType } from "../../typing/shared";

interface WebscrapeInput {
  url: string;
  recursionDepth: number | null;
  maxPageToScrape: number | null;
}

const initialData: WebscrapeInput = {
  url: "",
  recursionDepth: null,
  maxPageToScrape: null,
};

export default function WebsiteTabContent({
  service,
  setActiveScreen,
  setActiveTab,
  setBannerState,
  sitemapEnabled,
}: {
  setActiveTab: (val: string) => void;
  sitemapEnabled: boolean;
  service: WebScraperIntegration;
  setBannerState: React.Dispatch<React.SetStateAction<BannerState>>;
  setActiveScreen: React.Dispatch<React.SetStateAction<"UPLOAD" | "FILES">>;
}) {
  const [showSuccessState, setShowSuccessState] = useState<boolean>(false);
  const [websiteDataList, setWebsiteDataList] = useState<WebscrapeInput[]>([
    initialData,
  ]);
  const [submitting, setSubmitting] = useState(false);

  const {
    accessToken,
    apiURL,
    authenticatedFetch,
    chunkSize,
    embeddingModel,
    environment = ENV.PRODUCTION,
    generateSparseVectors,
    maxItemsPerChunk,
    onError,
    onSuccess,
    overlapSize,
    prependFilenameToChunks,
    tags,
  } = useCarbon();

  const maxPagesToScrape = service.maxPagesToScrape ?? MAX_PAGES_TO_SCRAPE;
  const maxRecursionDepth = service.recursionDepth ?? MAX_RECURSION_DEPTH;

  const updateWebsiteListData = (
    index: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newValues: { [key: string]: any },
  ) => {
    setWebsiteDataList((prev: WebscrapeInput[]) => {
      const newData = [...prev];

      newData[index] = { ...prev[index], ...newValues };

      return newData;
    });
  };

  const deleteWebsiteListData = (index: number) => {
    setWebsiteDataList((prev: WebscrapeInput[]) => {
      const newData = prev.filter((_, i) => i !== index);

      if (newData.length === 0) {
        return [initialData];
      }

      return newData;
    });
  };

  const submitScrape = async () => {
    try {
      if (submitting) {
        setBannerState({
          type: "ERROR",
          message: "Please wait for the request to finish",
        });

        return;
      }

      const chunkSizeValue =
        service?.chunkSize || chunkSize || DEFAULT_CHUNK_SIZE;
      const overlapSizeValue =
        service?.overlapSize || overlapSize || DEFAULT_OVERLAP_SIZE;
      const skipEmbeddingGeneration = service?.skipEmbeddingGeneration || false;
      const enableAutoSync = service?.enableAutoSync ?? false;
      const generateSparseVectorsValue =
        service?.generateSparseVectors ?? generateSparseVectors ?? false;
      const prependFilenameToChunksValue =
        service?.prependFilenameToChunks ?? prependFilenameToChunks ?? false;
      const maxItemsPerChunkValue =
        service?.maxItemsPerChunk || maxItemsPerChunk || null;
      const embeddingModelValue =
        service?.embeddingModel || embeddingModel || null;

      const htmlTagsToSkip = service?.htmlTagsToSkip || [];
      const cssClassesToSkip = service?.cssClassesToSkip || [];
      const cssSelectorsToSkip = service?.cssSelectorsToSkip || [];
      const downloadCssAndMedia = service?.downloadCssAndMedia || false;
      const generateChunksOnly = service?.generateChunksOnly || false;

      setSubmitting(true);

      const validData = websiteDataList.filter((urlData) =>
        isValidHttpUrl("https://" + urlData.url),
      );

      if (validData.length === 0) {
        setBannerState({
          type: "ERROR",
          message: "Please provide at least one valid URL.",
        });
        setSubmitting(false);

        return;
      }

      const requestObject = validData.map((urlData) => ({
        url: urlData.url,
        tags: tags,
        recursion_depth: urlData.recursionDepth ?? DEFAULT_RECURSION_DEPTH,
        max_pages_to_scrape: urlData.maxPageToScrape || MAX_PAGES_TO_SCRAPE,
        chunk_size: chunkSizeValue,
        chunk_overlap: overlapSizeValue,
        skip_embedding_generation: skipEmbeddingGeneration,
        enable_auto_sync: enableAutoSync,
        generate_sparse_vectors: generateSparseVectorsValue,
        prepend_filename_to_chunks: prependFilenameToChunksValue,
        html_tags_to_skip: htmlTagsToSkip,
        css_classes_to_skip: cssClassesToSkip,
        css_selectros_to_skip: cssSelectorsToSkip,
        ...(maxItemsPerChunkValue && {
          max_items_per_chunk: maxItemsPerChunkValue,
        }),
        ...(embeddingModelValue && {
          embedding_model: embeddingModelValue,
        }),
        download_css_and_media: downloadCssAndMedia,
        generate_chunks_only: generateChunksOnly,
      }));

      const uploadResponse = await authenticatedFetch(
        `${getBaseURL(apiURL, environment)}/web_scrape`,
        {
          method: "POST",
          headers: {
            Authorization: `Token ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestObject),
        },
      );
      const responseData = await uploadResponse.json();

      if (uploadResponse.status === 200) {
        setShowSuccessState(true);
        setWebsiteDataList([]);
        onSuccess &&
          onSuccess({
            status: 200,
            data: {
              data_source_external_id: null,
              sync_status: null,
              files: responseData,
            },
            action: ActionType.UPDATE,
            event: ActionType.UPDATE,
            integration: IntegrationName.WEB_SCRAPER,
          });
        setActiveScreen("FILES");
        setBannerState({
          message: "Scraping request initiated successfully.",
          type: "SUCCESS",
        });
      } else {
        setBannerState({
          type: "ERROR",
          message: responseData.detail,
        });
      }
    } catch (error) {
      setBannerState({
        type: "ERROR",
        message: "Error initiating scraping. Please try again.",
      });

      onError &&
        onError({
          status: 400,
          data: [{ message: "Error initiating scraping. Please try again." }],
          action: ActionType.UPDATE,
          event: ActionType.UPDATE,
          integration: IntegrationName.WEB_SCRAPER,
        });
      setShowSuccessState(false);
    }
    setSubmitting(false);
  };

  if (showSuccessState) {
    return (
      <div className="cc-flex-grow cc-p-4 cc-overflow-auto cc-flex cc-flex-col">
        <WebScraperTabs
          activeTab="website"
          setActiveTab={setActiveTab}
          sitemapEnabled={sitemapEnabled}
        />
        <SuccessState
          action={() => setShowSuccessState(false)}
          heading="Scraping request initiated successfully."
        />
      </div>
    );
  }

  return (
    <>
      <div className="cc-flex-grow cc-p-4 cc-overflow-auto">
        <WebScraperTabs
          activeTab="website"
          setActiveTab={setActiveTab}
          sitemapEnabled={sitemapEnabled}
        />
        <div className="cc-pb-4 cc-flex cc-grow cc-w-full">
          <div className="cc-flex cc-flex-col cc-justify-start cc-items-start cc-w-full cc-space-y-4">
            {websiteDataList.map((websiteData, index) => (
              <div
                className="cc-flex cc-space-x-2 cc-items-center cc-w-full cc-h-10"
                key={index}
              >
                <div className="cc-flex cc-flex-1 cc-relative">
                  <div>
                    <img
                      alt="tabler_sitemap"
                      className="cc-absolute cc-top-3 cc-left-2 cc-pointer-events-none dark:cc-invert-[1] dark:cc-hue-rotate-180"
                      src={images.left_icon}
                    />
                    <Input
                      className="cc-w-[100px_!important] !cc-pl-8 !cc-rounded-r-none"
                      disabled
                      placeholder="Enter URL"
                      type="text"
                      value="https://"
                    />
                  </div>
                  <div className="cc-w-px cc-shrink-0 cc-bg-outline-med_em dark:cc-bg-dark-input-bg " />
                  <Input
                    className="cc-rounded-l-none"
                    onChange={(e) =>
                      updateWebsiteListData(index, {
                        url: removeHttp(e.target.value),
                      })
                    }
                    placeholder="Enter URL"
                    type="text"
                    value={websiteData.url}
                  />
                </div>
                <div className="cc-flex sm:cc-hidden cc-relative">
                  <MobileWebsiteUrlDropdown
                    deleteUrl={
                      websiteDataList.length === 1 &&
                      websiteDataList[0].url === ""
                        ? undefined
                        : () => deleteWebsiteListData(index)
                    }
                    index={index}
                    initialData={websiteData}
                    maxPagesToScrape={maxPagesToScrape}
                    maxRecursionDepth={maxRecursionDepth}
                    updateWebsiteListData={updateWebsiteListData}
                  />
                </div>
                <div className="cc-hidden sm:cc-flex cc-items-center cc-gap-3">
                  <FilterPopover
                    index={index}
                    initialData={websiteData}
                    maxPagesToScrape={maxPagesToScrape}
                    maxRecursionDepth={maxRecursionDepth}
                    updateWebsiteListData={updateWebsiteListData}
                  />
                  <Button
                    className="cc-px-[0_!important] cc-w-10 cc-border-transparent"
                    size="md"
                    variant="neutral-white"
                  >
                    <img
                      alt=""
                      className="cc-cursor-pointer dark:cc-invert-[1] dark:cc-hue-rotate-180"
                      onClick={() => deleteWebsiteListData(index)}
                      src={images.trash_2}
                    />
                  </Button>
                </div>
              </div>
            ))}
            {websiteDataList.length < 50 && (
              <>
                <Button
                  className="cc-w-full cc-flex sm:cc-hidden"
                  onClick={() => {
                    setWebsiteDataList((prev) => [...prev, initialData]);
                  }}
                  size="md"
                  variant="neutral-white"
                >
                  <img
                    className="cc-w-[18px] cc-h-[18px] cc-shrink-0"
                    src={images.addIcon}
                  />
                  Add
                </Button>
                <Button
                  className="cc-w-full cc-hidden sm:cc-flex"
                  onClick={() => {
                    setWebsiteDataList((prev) => [...prev, initialData]);
                  }}
                  size="lg"
                  variant="neutral-white-fix"
                >
                  <img
                    className="cc-w-5 cc-h-5 cc-shrink-0"
                    src={images.addIcon}
                  />
                  Add
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <DialogFooter>
        <div className="cc-mb-4 cc-full cc-text-sm cc-flex cc-justify-center cc-text-low_em cc-font-semibold dark:cc-text-dark-text-gray">
          <img
            alt="info_fill"
            className="cc-h-5 cc-w-5 cc-flex cc-mr-2"
            src={images.info_fill}
          />
          First {maxPagesToScrape} links per website are synced by default.
        </div>
        <Button
          className="cc-w-full"
          disabled={submitting}
          onClick={() => {
            submitScrape();
          }}
          size="md"
        >
          Submit
        </Button>
      </DialogFooter>
    </>
  );
}

function FilterPopover({
  index,
  initialData,
  maxPagesToScrape,
  maxRecursionDepth,
  updateWebsiteListData,
}: {
  initialData: WebscrapeInput;
  index: number;
  updateWebsiteListData: (
    index: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newValues: { [key: string]: any },
  ) => void;
  maxPagesToScrape: number;
  maxRecursionDepth: number;
}) {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className="cc-gap-2 cc-font-semibold cc-relative"
          size="md"
          type="button"
          variant="neutral-white"
        >
          <img
            alt=""
            className="cc-mr-2 dark:cc-invert-[1] dark:cc-hue-rotate-180"
            src={images.filter}
          />
          Configure
          <img
            alt=""
            className="dark:cc-invert-[1] dark:cc-hue-rotate-180"
            src={DownChevIcon}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="cc-p-2 cc-hidden sm:!cc-block">
        <ConfigureForm
          close={() => setOpen(false)}
          index={index}
          initialData={initialData}
          maxPagesToScrape={maxPagesToScrape}
          maxRecursionDepth={maxRecursionDepth}
          updateWebsiteListData={updateWebsiteListData}
        />
      </PopoverContent>
    </Popover>
  );
}

function MobileWebsiteUrlDropdown({
  deleteUrl,
  index,
  initialData,
  maxPagesToScrape,
  maxRecursionDepth,
  updateWebsiteListData,
}: {
  initialData: WebscrapeInput;
  index: number;
  updateWebsiteListData: (
    index: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newValues: { [key: string]: any },
  ) => void;
  deleteUrl?: () => void;
  maxPagesToScrape: number;
  maxRecursionDepth: number;
}) {
  const [showDialog, setShowDialog] = useState<boolean>(false);

  return (
    <>
      <Dialog onOpenChange={setShowDialog} open={showDialog}>
        <DialogContent
          activeState={"INTEGRATION_LIST"}
          className="cc-h-auto sm:cc-h-fit sm:cc-max-h-[90vh] cc-top-auto  cc-gap-0 sm:cc-rounded-[20px] cc-translate-y-0 -sm:cc-translate-y-1/2 cc-bottom-0 cc-rounded-t-2xl sm:cc-bottom-auto sm:top-1/2 cc-overflow-visible"
          style={{ height: "auto" }}
        >
          <DialogClose asChild>
            <Button
              className="cc-absolute -cc-top-14 cc-left-1/2 cc-px-[0_!important] cc-w-10 -cc-translate-x-1/2"
              onClick={() => setShowDialog(false)}
              size="md"
              variant="neutral-white"
            >
              <img
                alt="CrossIcon"
                className="cc-h-[18px] cc-w-[18px]"
                src={images.crossIcon}
              />
            </Button>
          </DialogClose>
          <div className="cc-p-4 flex flex-col gap-8">
            <div className="cc-text-xlxt cc-font-bold cc-mb-2">Configure</div>
            <ConfigureForm
              buttonVariant="primary"
              close={() => setShowDialog(false)}
              index={index}
              initialData={initialData}
              maxPagesToScrape={maxPagesToScrape}
              maxRecursionDepth={maxRecursionDepth}
              updateWebsiteListData={updateWebsiteListData}
            />
          </div>
        </DialogContent>
      </Dialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <img
            alt="Menu Icon"
            className="cc-h-[18px] cc-w-[18px] cc-shrink-0"
            src={images.menuIcon}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="cc-w-48 cc-bg-white cc-border cc-py-[4px] cc-border-outline-base_em cc-rounded-xl cc-shadow-e3 cc-z-30 cc-block sm:!cc-hidden"
        >
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="cc-flex cc-justify-between cc-font-semibold !cc-py-[8px] !cc-px-[20px] cc-cursor-pointer "
              onClick={() => setShowDialog(true)}
            >
              <span>Configure</span>
              <img
                alt=""
                className="cc-w-[18px] cc-h-[18px]"
                src={images.filter}
              />
            </DropdownMenuItem>
            {deleteUrl && <DropdownMenuSeparator />}
            {deleteUrl && (
              <DropdownMenuItem
                className="cc-flex cc-justify-between cc-font-semibold !cc-py-[8px] !cc-px-[20px] cc-cursor-pointer"
                onClick={deleteUrl}
              >
                <span>Delete</span>
                <img
                  alt=""
                  className="cc-w-[18px] cc-h-[18px dark:cc-invert-[1] dark:cc-hue-rotate-180"
                  src={images.trash_2}
                />
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

function ConfigureForm({
  close,
  index,
  initialData,
  maxPagesToScrape,
  maxRecursionDepth,
  updateWebsiteListData,
}: {
  initialData: WebscrapeInput;
  index: number;
  updateWebsiteListData: (
    index: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newValues: { [key: string]: any },
  ) => void;
  close: () => void;
  buttonVariant?: "neutral-white" | "primary";
  maxPagesToScrape: number;
  maxRecursionDepth: number;
}) {
  const [urlData, setUrlData] = useState(initialData);

  return (
    <>
      <div className="cc-flex cc-justify-between cc-items-center cc-py-2 ">
        <div className="cc-flex">
          <label>
            <span
              className={cn(
                `dark:after:cc-bg-dark-bg-black cc-custom-radio cc-text-sm cc-font-semibold cc-text-high_em dark:cc-text-dark-text-white dark:before:cc-border-dark-text-gray before:cc-hidden `,
              )}
            >
              Recursion depth
            </span>
          </label>
        </div>
        <div className="cc-w-[51px]">
          <Input
            className="cc-h-8 cc-text-xs !cc-pl-2"
            onChange={(e) => {
              if (parseInt(e.target.value) > (maxRecursionDepth || 10)) return;

              setUrlData((prev) => ({
                ...prev,
                recursionDepth: parseInt(e.target.value) || 0,
                selectedFilter: "recursionDepth",
              }));
            }}
            placeholder=""
            type="text"
            value={urlData.recursionDepth || 0}
          />
        </div>
      </div>
      <div className="cc-flex cc-justify-between cc-items-center cc-py-2">
        <div className="cc-flex">
          <label>
            <span
              className={cn(
                `cc-custom-radio cc-text-sm cc-font-semibold cc-text-high_em dark:cc-text-dark-text-white dark:before:cc-border-dark-text-gray before:cc-hidden`,
              )}
            >
              Max pages to scrape
            </span>
          </label>
        </div>
        <div className="cc-w-[51px]">
          <Input
            className="cc-h-8 cc-text-xs !cc-pl-2"
            onChange={(e) => {
              if (parseInt(e.target.value) > maxPagesToScrape) return;

              setUrlData((prev) => ({
                ...prev,
                maxPageToScrape: parseInt(e.target.value) || 0,
              }));
            }}
            placeholder=""
            type="text"
            value={urlData.maxPageToScrape || 0}
          />
        </div>
      </div>
      <Button
        className="cc-font-semibold cc-w-full cc-mt-3"
        onClick={() => {
          updateWebsiteListData(index, {
            recursionDepth: urlData.recursionDepth,
            maxPageToScrape: urlData.maxPageToScrape,
          });
          close();
        }}
        size="md"
        variant="neutral-white-fix"
      >
        Apply
      </Button>
    </>
  );
}
